import React, { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const API = process.env.REACT_APP_BACKEND_URL;

export const EYE_ICON_URL = "/sidebar-logo.png";

export const GENRES = [
  'Pop', 'Rock', 'Hip-Hop / Rap', 'R&B / Soul', 'Electronic / Dance (EDM)', 
  'Jazz', 'Classical', 'Country', 'Reggae', 'Blues', 'Latin', 'Folk', 
  'Metal', 'Punk', 'Funk', 'Gospel / Christian', 'Indie / Alternative', 
  'K-Pop', 'Afrobeats', 'World Music', 'Other'
];

export const VIDEO_STYLES = ['Story', 'Perform', 'Dance', 'FX', 'POV', 'Life', 'Vibe', 'Hook', 'Hype'];

export const FILM_GENRES = ['Drama', 'Documentary', 'Sci-Fi', 'Animation', 'Experimental', 'Short Film'];

export const FILM_ROLES = ['Director', 'Writer', 'Producer', 'Editor', 'Composer', 'Cinematographer', 'Voice', 'Cast', 'Animator', 'VFX'];

// Reusable "Suggest with AI" control. Builds context from whatever the creator
// has typed so far, calls POST /api/ai/suggest-metadata, and hands the result
// back to the caller so it can autofill editable fields. Stubbed in the fork.
export const SuggestAIButton = ({ axiosInstance, getContext, contentType, onResult, disabled }) => {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    const context = (getContext() || '').trim();
    if (!context) {
      toast.error('Type a title or a few notes first, then let AI expand it.');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.post('/ai/suggest-metadata', { context, content_type: contentType });
      onResult(res.data || {});
      toast.success('AI suggestions added — edit them freely.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      type="button"
      data-testid={`suggest-ai-${contentType}`}
      onClick={run}
      disabled={disabled || loading}
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 border border-orange-500/30 transition-colors disabled:opacity-50"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {loading ? 'Thinking…' : 'Suggest with AI'}
    </button>
  );
};

// Small chip row showing AI-suggested tags. Clicking a chip appends "#tag" to
// the target field so suggestions remain fully editable by the creator.
export const SuggestedTags = ({ tags, onPick }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="ai-suggested-tags">
      {tags.map((t, i) => (
        <button
          key={t}
          type="button"
          data-testid={`ai-tag-${i}`}
          onClick={() => onPick(t)}
          className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 hover:bg-orange-500/20 hover:text-orange-300 border border-zinc-700 transition-colors"
        >
          #{t}
        </button>
      ))}
    </div>
  );
};

// Inline editor that lets a creator pick the 20-40s hook window Pulse plays.
// Format watch time without flooring sub-minute values to "0m". AudIVue clips
// are short (often 30-60s) so most of a creator's early views ARE under one
// minute — collapsing them to 0 was the entire reason the dashboard always
// read "0 minutes". Display rules:
//   • >= 1 hour → "Hh Mm"
//   • >= 1 min  → "Mm Ss" (no zero seconds)
//   • >= 1 sec  → "Xs"
//   • 0         → "0s"  (NOT "0m" — that's what hid the bug)
export const formatWatchTime = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${m}m ${rest}s` : `${m}m`;
  }
  return `${s}s`;
};

export const MOCK_THUMBNAILS = [
  "https://images.unsplash.com/photo-1762279801044-3c37872f4534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1764258906659-e966a089ed7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdsb3dpbmclMjBnZW9tZXRyaWMlMjBmbHVpZHxlbnwwfHx8fDE3NzUxMzczNjZ8MA&ixlib=rb-4.1.0&q=85",
];
